import { css } from '@emotion/react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import Spinner from '~/common/components/utils/Spinner.tsx';
import { useAppDispatch } from '~/common/stores';
import { FONT_SIZE, FONT_WEIGHT, SPACING, TOKEN } from '~/configs/theme.ts';
import useProfile from '~/features/profile/hooks/useProfile.ts';
import { fetchMyProfile } from '~/features/profile/stores/profile.slice.ts';

import type { PropsWithChildren } from 'react';

import type { UserRole } from '~/features/profile/types/profile.type';

interface RoleGuardProps extends PropsWithChildren {
  /** 이 화면이 요구하는 역할 */
  allow: UserRole;
}

/** 역할별 안내 문구 — 무엇이 부족한지 말해주지 않으면 사용자는 오류로 오해한다. */
const LABEL: Record<UserRole, { title: string; desc: string }> = {
  tutor: {
    title: '지도사 전용 화면입니다',
    desc: '지도사로 가입한 계정에서만 이용할 수 있습니다.',
  },
  admin: {
    title: '운영자 전용 화면입니다',
    desc: '운영 권한이 있는 계정에서만 이용할 수 있습니다.',
  },
  guardian: {
    title: '보호자 전용 화면입니다',
    desc: '보호자 계정에서만 이용할 수 있습니다.',
  },
};

/**
 * 역할 전용 화면 진입 안내.
 *
 * @description **보안 장치가 아니다.** 역할을 클라이언트에서 확인하는 것만으로는 아무것도 막지 못한다
 *  (응답을 조작하면 통과한다). 실제 차단은 RLS 가 한다 — 지도사가 아닌 계정이 지도사용 데이터를
 *  요청하면 DB 가 빈 결과를 준다.
 *
 *  그래서 이 컴포넌트의 목적은 **설명**이다. 가드가 없으면 보호자가 `/tutor-console` 주소로 들어왔을 때
 *  "빈 목록"만 보게 되는데, 그건 오류인지 데이터가 없는 건지 구분되지 않는다. 이유를 말해준다.
 *
 *  판정 전에는 아무것도 단정하지 않는다 — 조회가 끝나기 전에 튕기면 정상 진입도 거부 화면이 스쳐 보인다.
 */
function RoleGuard({ allow, children }: RoleGuardProps) {
  const dispatch = useAppDispatch();
  const { me, isMeLoaded } = useProfile();

  useEffect(() => {
    if (!isMeLoaded) dispatch(fetchMyProfile());
  }, [dispatch, isMeLoaded]);

  if (!isMeLoaded) return <Spinner fullScreen />;

  if (me?.role !== allow) {
    return (
      <div css={styles.wrap}>
        <span css={styles.icon} aria-hidden>
          🔒
        </span>
        <h1 css={styles.title}>{LABEL[allow].title}</h1>
        <p css={styles.desc}>{LABEL[allow].desc}</p>
        <Link css={styles.link} to="/">
          홈으로
        </Link>
      </div>
    );
  }

  return children;
}

const styles = {
  wrap: css`
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: ${SPACING[10]} ${SPACING[6]};
    text-align: center;
  `,
  icon: css`
    font-size: 44px;
  `,
  title: css`
    margin: ${SPACING[5]} 0 ${SPACING[3]};
    color: ${TOKEN.textPrimary};
    font-size: ${FONT_SIZE[18]};
    font-weight: ${FONT_WEIGHT.BOLD};
  `,
  desc: css`
    margin: 0 0 ${SPACING[8]};
    color: ${TOKEN.textSecondary};
    font-size: ${FONT_SIZE[14]};
    line-height: 1.7;
  `,
  link: css`
    color: ${TOKEN.primary};
    font-size: ${FONT_SIZE[14]};
    font-weight: ${FONT_WEIGHT.SEMI_BOLD};
  `,
};

export default RoleGuard;
